import React, {useEffect, useState} from "react";
import {Container} from "../containers/Container";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  fetchOrders,
  reset,
  selectError,
  selectIsLoading,
  selectOrders,
  selectTotalResults
} from "../../redux/orderList/orderListSlice";
import {HeroHeading2} from "../text";
import {useTranslation} from "next-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import {LoadingSpinnerIcon, WarningColorIcon} from "../icons";
import {OrderListItemView} from "./OrderListItemView";
import Skeleton from "react-loading-skeleton";
import {useRouter} from "next/router";
import {useModalRouting} from "../../utils/useModalRouting";
import {StoreEmptyStateView} from "../storeFeed/StoreEmptyStateView";
import {ApiError} from "../../errors/apiError";
import {RealmServiceError} from "../../errors/realmServiceError";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const OrderListView: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [pageNumber, setPageNumber] = useState(1)
  const orders = useAppSelector(selectOrders)
  const totalResults = useAppSelector(selectTotalResults)
  const isLoading = useAppSelector(selectIsLoading)
  const error = useAppSelector(selectError)
  const router = useRouter()
  const { createRoute } = useModalRouting()

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Order List' }))
  }, [])

  useEffect(() => {
    dispatch(reset())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchOrders(pageNumber))
  }, [dispatch, pageNumber])

  const emptyStateButtonTitle = error instanceof ApiError && error.code === 'UNAUTHORIZED' || error instanceof RealmServiceError && error.code === 'NOT_LOGGED_IN'
    ? t('auth:auth_title_sign_in')
    : t('store:store_empty_state_button_try_again')

  return <Container className="mb-32">
    <div className={`${error ? '' : 'hidden'} flex-1 flex flex-col justify-center items-center`}>
      <StoreEmptyStateView
        onClick={() => {
          if (error instanceof ApiError && error.code === 'UNAUTHORIZED' || error instanceof RealmServiceError && error.code === 'NOT_LOGGED_IN') {
            router.push(
              createRoute({ name: 'login', query: { config: 'sign_in' }, reloadOnClose: true }),
              undefined,
              { shallow: true }
            );
          } else {
            dispatch(reset())
            dispatch(fetchOrders(pageNumber))
          }
        }}
        buttonTitle={emptyStateButtonTitle}
        error={error}
        icon={<WarningColorIcon />}
        isLoading={isLoading}
      />
    </div>

    <HeroHeading2 className={`mb-8 ${error ? 'hidden' : ''}`}>{isLoading ? <Skeleton className="!w-2/5" /> : t('store:store_title_past_orders')}</HeroHeading2>

    <div className={`${error ? 'hidden' : ''}`}>
      <InfiniteScroll
        next={() => {
          setPageNumber(pageNumber + 1)
        }}
        hasMore={orders.length < totalResults && !error}
        loader={
          <div className="w-full py-6"><LoadingSpinnerIcon className="animate-spin h-8 w-8 text-fg-primary mx-auto" /></div>
        }
        dataLength={orders.length}
      >
        {orders.map(order => {
          return <div key={order.id} className="border-b last:border-0 border-fg-gray-surface">
            <OrderListItemView order={order} isLoading={isLoading} />
          </div>
        })}
      </InfiniteScroll>
    </div>
  </Container>
}